import TemplateElementWarningsService from "./template_element_warnings_service";

class ElementWarningsService {
  static getWarning(element, variant, step = null) {
    if (!element || !variant) return undefined;
    if (step == null) {
      const section = variant.sections[element?.section_id];
      step = variant.steps[section?.step_id];
    }
    if (!step) return undefined;
    if (element.toBeDeleted) return undefined;

    // Check if element's column is being used by the section
    const section = variant.sections[element?.section_id];
    if (!this.isElementColumnBeingUsed(element, section)) return undefined;

    const conversionElements = [
      "button",
      "form",
      "survey",
      "quiz",
      "products",
      "product_hero",
    ];
    if (!conversionElements.includes(element.element_type)) return undefined;

    const belongsToFunnelStep = Object.values(variant.sections || {})
      .filter((section) => {
        if (!section) return false;
        return section.step_id == step.id && section.toBeDeleted !== true;
      })
      .map((section) => section.id)
      .includes(element?.section_id);

    if (!belongsToFunnelStep) return undefined;

    let warning = this.getConfirmationWarning(element, variant);

    if (["products", "product_hero"].includes(element.element_type)) {
      warning = this.getProductWarning(element, variant);
    }

    return warning;
  }

  static isElementColumnBeingUsed(element, section) {
    if (!element || !section || !element.column) return false;
    
    const desktopColumnLayout = section.options?.structure?.["column-layout"] || "100";
    const mobileColumnLayout = section.options?.structure?.["column-layout-mobile"];
    const isMobileResponsive = section.options?.structure?.["mobile-responsive"] !== "false";
    const elementColumn = element.column;

    // Check if column is used in desktop layout
    const isUsedInDesktop = this.isColumnUsedInLayout(elementColumn, desktopColumnLayout);
    
    // Check if column is used in mobile layout (if mobile has separate layout)
    const isUsedInMobile = mobileColumnLayout && !isMobileResponsive 
      ? this.isColumnUsedInLayout(elementColumn, mobileColumnLayout)
      : isUsedInDesktop; // Use desktop layout if mobile is responsive or no mobile layout set

    // Element should show warnings if it's used in either desktop OR mobile layout
    return isUsedInDesktop || isUsedInMobile;
  }

  static isColumnUsedInLayout(columnNumber, columnLayout) {
    // First column is always used
    if (columnNumber === 1) return true;

    // Second column is used if layout is not "100"
    if (columnNumber === 2) {
      return !["100"].includes(columnLayout);
    }

    // Third column is used for these layouts
    if (columnNumber === 3) {
      return ["33-33-33", "50-25-25", "25-25-50", "25-50-25", "25-25-25-25"].includes(columnLayout);
    }

    // Fourth column is only used for this layout
    if (columnNumber === 4) {
      return ["25-25-25-25"].includes(columnLayout);
    }

    return false;
  }

  static isValidRedirectStep(redirectStep, variant, currentStepId = null) {
    if (!redirectStep) return false;
    if (redirectStep === "previous") return true;
    if (
      currentStepId != null &&
      redirectStep + "" === currentStepId + ""
    ) {
      return false;
    }

    return Object.values(variant?.steps || {}).some(
      (s) => s && s.id + "" === redirectStep + "" && s.toBeDeleted !== true
    );
  }

  static currentStepIdForElement(element, variant) {
    const section = variant?.sections?.[element?.section_id];
    return section?.step_id ?? element?.step_id ?? null;
  }

  static getConfirmationWarning(element, variant) {
    const confirmationType = element.options["confirmation-type"];
    const currentStepId = this.currentStepIdForElement(element, variant);

    switch (confirmationType) {
      case "step": {
        const redirectStep = element.options["redirect-step"];
        if (!redirectStep) return "Step to jump to not yet set";

        if (!this.isValidRedirectStep(redirectStep, variant, currentStepId)) {
          return "Step to jump to not yet set";
        }
        break;
      }
      case "redirect": {
        if (
          !element.options["redirect-url"] &&
          element.options["confirmation-close"] !== "true"
        ) {
          return "Redirect URL not yet set";
        }
        break;
      }
      case "cta": {
        if (!element.options["confirmation-cta"])
          return "Campaign to display not yet set";
        break;
      }
      case "sms": {
        if (!element.options["sms-number"])
          return "SMS opt-in information incomplete";
        break;
      }
      case "cart":
      case "checkout": {
        if (!['products', 'product_hero'].includes(element.element_type)) {
          if (!element.options["selected-store-integration"])
            return "No ecommerce platform selected";

          if (element.options["selected-store-integration"] !== null && !element.options["selected-product-variant-ids"])
            return "No product variants selected to add to cart"
        }
        break;
      }
      default:
        break;
    }

    return undefined;
  }

  // Warning count for the funnel tab badge (no step) or a step thumbnail
  // (scoped to that step): elements with confirmation/template warnings plus
  // step-level action groups needing reconfiguration.
  static getWarningCount(variant, step = null) {
    const elementCount = Object.values(variant.elements || {}).filter(
      (element) => {
        if (!element) return false;
        if (this.getWarning(element, variant, step)) return true;

        const section = variant.sections?.[element.section_id];
        if (!section || section.toBeDeleted === true) return false;
        if (step && section.step_id != step.id) return false;

        return TemplateElementWarningsService.elementHasWarning(
          element,
          variant
        );
      }
    ).length;

    const groupCount = Object.values(variant.action_groups || {}).filter(
      (group) => {
        if (!group) return false;
        if (!group.step_id || group.toBeDeleted) return false;
        if (step && group.step_id != step.id) return false;

        const groupStep = variant.steps?.[group.step_id];
        if (!groupStep || groupStep.toBeDeleted === true) return false;

        return TemplateElementWarningsService.getActionGroupRollupWarning(
          group,
          variant
        );
      }
    ).length;

    return elementCount + groupCount;
  }

  static getProductWarning(element, variant) {
    const confirmationType = element.options["confirmation-type"];
    const actionAfterAddToCart = element.options["products-button-action-after-add-to-cart"];
    const currentStepId = this.currentStepIdForElement(element, variant);

    if (confirmationType !== "cart") return undefined;

    switch (actionAfterAddToCart) {
      case "step": {
        const redirectStep = element.options["products-button-redirect-step"];
        if (!redirectStep) return "Step to jump to not yet set";
        if (!this.isValidRedirectStep(redirectStep, variant, currentStepId)) {
          return "Step to jump to not yet set";
        }
        break;
      }
      case "redirect": {
        if (!element.options["products-button-redirect-url"])
          return "Redirect URL not yet set";
        break;
      }
      case "cta": {
        if (!element.options["products-button-confirmation-cta"])
          return "Campaign to display not yet set";
        break;
      }
      default:
        break;
    }

    return undefined;
  }
}

if (typeof window !== 'undefined' && typeof window.convertflow == 'undefined') {
  window.ElementWarningsService = ElementWarningsService;
}
